import { sendToApi } from 'api/client'
import { AlmanacFileShowResponse, AlmanacFilesIndexResponse } from 'hooks/queries/files/types'
import qs from 'qs'
import { PostStatusPropertyValue } from './PropertyClient'

export type RansackQ = Record<string, string | string[] | number[] | Date | boolean>

interface IndexParams {
  page?: number
  items?: number
  q?: RansackQ
  project_key?: string
}

interface ShowParams {
  q?: RansackQ
}

export interface UpdateAlmanacFileInput {
  title?: string | null
  post_at?: string | null
  post_status?: string | null
  view_metadata?: {
    view: 'calendar_view' | 'list_view'
  }
  reviewed_at?: string | null
}

export const PROPERTY_STATUS_TO_COLUMN_VALUE: Record<PostStatusPropertyValue, number> = {
  [PostStatusPropertyValue.Draft]: 0,
  [PostStatusPropertyValue.ReminderScheduled]: 1,
  [PostStatusPropertyValue.AutoPostScheduled]: 2,
  [PostStatusPropertyValue.Posted]: 3,
  [PostStatusPropertyValue.Failed]: 4,
}

export type Sorting =
  | 'post_at desc'
  | 'post_at asc'
  | 'title desc'
  | 'title asc'
  | 'post_status desc'
  | 'post_status asc'

export const buildFilesQuery = ({
  channels,
  statuses,
  externalIntegrationIds,
  projectKeys,
  folderId,
  sorting,
  startDate,
  endDate,
  campaignId,
  needsApproval,
}: {
  channels?: string[]
  statuses?: string[]
  externalIntegrationIds?: number[]
  projectKeys?: string[]
  folderId?: string
  sorting?: Sorting
  startDate?: string | null
  endDate?: string
  campaignId?: number
  needsApproval?: boolean
}) => {
  const query: RansackQ = {}

  if (startDate) {
    query.post_at_gteq = startDate
  }
  if (endDate) {
    query.post_at_lteq = endDate
  }
  if (channels && channels.length > 0) {
    query.channel_in = channels
  }
  if (statuses && statuses.length > 0) {
    query.post_status_in = statuses.map((status) => PROPERTY_STATUS_TO_COLUMN_VALUE[status as PostStatusPropertyValue])
  }
  if (externalIntegrationIds && externalIntegrationIds.length > 0) {
    query.external_integration_id_in = externalIntegrationIds
  }
  if (folderId) {
    query.by_project = folderId
  }
  if (projectKeys && projectKeys.length > 0) {
    query.project_key_in = projectKeys
  }
  if (sorting) {
    query.s = sorting
  }
  if (campaignId) {
    query.campaign_id_eq = campaignId.toString()
  }
  if (needsApproval) {
    query.needs_approval = 'true'
  }

  return query
}

export const AlmanacFilesClient = {
  index: async (workspaceId: number, params: IndexParams = {}) => {
    return sendToApi<AlmanacFilesIndexResponse>(`/w/${workspaceId}/almanac_files`, {
      method: 'GET',
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  recents: async (workspaceId: number, params: IndexParams = {}) => {
    return sendToApi<AlmanacFilesIndexResponse>(`/w/${workspaceId}/almanac_files/recents`, {
      method: 'GET',
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  show: async (workspaceId: number, key: string, params?: ShowParams) => {
    return sendToApi<AlmanacFileShowResponse>(`/w/${workspaceId}/almanac_files/${key}`, {
      method: 'GET',
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  update: async (workspaceId: number, key: string, payload: UpdateAlmanacFileInput) => {
    return sendToApi<AlmanacFileShowResponse>(`/w/${workspaceId}/almanac_files/${key}`, {
      method: 'PUT',
      body: { almanac_file: payload },
    })
  },
}
